import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import type {
  OpsView,
  OpsWhoAmIResponse,
  PaginatedResponse,
  ColumnSchema,
  SyncStatusEntry,
  PhotoEntry,
  AuditEntry,
  FilterCondition,
  ViewStats,
  AggregationStats,
} from "../types/ops";
import { buildLoginUrl } from "../utils/auth";

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const PHOTO_STALE_TIME = 10 * 60 * 1000; // 10 minutes — SAS URLs expensive to mint
const SYNC_STALE_TIME = 30 * 1000; // 30 seconds

/**
 * Shared authenticated-JSON fetch helper. Exported so other hook modules
 * (e.g. `useMethodologyApi.ts`) reuse the same SWA auth-redirect handling
 * instead of duplicating it.
 */
export async function fetchJson<T>(url: string, options?: { redirectOnAuth?: boolean; init?: RequestInit }): Promise<T> {
  const requestInit = options?.init;
  const redirectOnAuth = options?.redirectOnAuth ?? true;
  const res = await fetch(url, {
    credentials: "same-origin",
    redirect: "manual",
    ...requestInit,
    headers: { Accept: "application/json", ...requestInit?.headers },
  });

  // SWA returns 0 (opaque redirect) or 401 when not authenticated
  if (res.status === 401 || res.type === "opaqueredirect" || res.status === 0) {
    if (!redirectOnAuth) {
      throw new Error("UNAUTHENTICATED");
    }

    const key = "ops_auth_redirect";
    const last = Number(sessionStorage.getItem(key) || "0");
    if (Date.now() - last < 30_000) {
      throw new Error("Authentication failed — please check your account has portal access");
    }
    sessionStorage.setItem(key, String(Date.now()));
    window.location.href = buildLoginUrl(window.location.pathname);
    throw new Error("Unauthorized — redirecting to login");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const error = new Error(body.error || `API error: ${res.status}`) as Error & { status?: number };
    // Attach the HTTP status so callers (e.g. methodology mutations) can react
    // to specific codes — 409 precondition-failed in particular — without
    // parsing message strings. Additive; existing callers only read `.message`.
    error.status = res.status;
    throw error;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

async function fetchWhoAmI(): Promise<OpsWhoAmIResponse> {
  try {
    return await fetchJson<OpsWhoAmIResponse>("/api/ops/whoami", { redirectOnAuth: false });
  } catch (error) {
    if (error instanceof Error && error.message === "UNAUTHENTICATED") {
      return { hasHeader: false, user: null };
    }
    throw error;
  }
}

export function useWhoAmI() {
  return useQuery<OpsWhoAmIResponse>({
    queryKey: ["ops", "whoami"],
    queryFn: fetchWhoAmI,
    staleTime: STALE_TIME,
  });
}

export function useViews() {
  return useQuery<{ data: OpsView[] }>({
    queryKey: ["ops", "views"],
    queryFn: () => fetchJson("/api/ops/views"),
    staleTime: STALE_TIME,
  });
}

export function useViewData(
  viewName: string | undefined,
  params: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: "asc" | "desc";
    filters?: FilterCondition[];
  } = {},
) {
  const { page = 1, limit = 50, sort, order = "desc", filters } = params;
  const searchParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    order,
  });
  if (sort) searchParams.set("sort", sort);
  if (filters && filters.length > 0)
    searchParams.set("filter", JSON.stringify({ filters }));

  return useQuery<PaginatedResponse<Record<string, unknown>>>({
    queryKey: ["ops", "views", viewName, "data", { page, limit, sort, order, filters }],
    queryFn: () => fetchJson(`/api/ops/views/${viewName}?${searchParams}`),
    enabled: !!viewName,
    staleTime: STALE_TIME,
  });
}

export function useViewSchema(viewName: string | undefined) {
  return useQuery<{
    viewName: string;
    label: string;
    columns: ColumnSchema[];
    recordStatusChoices?: Array<{ value: string; label: string }>;
  }>({
    queryKey: ["ops", "views", viewName, "schema"],
    queryFn: () => fetchJson(`/api/ops/views/${viewName}/schema`),
    enabled: !!viewName,
    staleTime: 30 * 60 * 1000, // 30 minutes — schema rarely changes
  });
}

export function useViewSummary(viewName: string | undefined) {
  return useQuery<{ viewName: string; label: string; total: number }>({
    queryKey: ["ops", "views", viewName, "summary"],
    queryFn: () => fetchJson(`/api/ops/views/${viewName}/summary`),
    enabled: !!viewName,
    staleTime: STALE_TIME,
  });
}

export function useViewStats(viewName: string | undefined) {
  return useQuery<ViewStats>({
    queryKey: ["ops", "views", viewName, "stats"],
    queryFn: () => fetchJson(`/api/ops/views/${viewName}/stats`),
    enabled: !!viewName,
    staleTime: STALE_TIME,
  });
}

export function useRecord(recordId: string | undefined) {
  return useQuery<{
    data: Record<string, unknown>;
    snapshotHash?: string;
    viewName: string;
    viewDef: { label: string; site: string; formId: string; writable: boolean };
  }>({
    queryKey: ["ops", "records", recordId],
    queryFn: () => fetchJson(`/api/ops/records/${recordId}`),
    enabled: !!recordId,
    staleTime: STALE_TIME,
  });
}

export function usePhotos(recordId: string | undefined) {
  return useQuery<{ recordId: string; photos: PhotoEntry[] }>({
    queryKey: ["ops", "photos", recordId],
    queryFn: () => fetchJson(`/api/ops/photos/${recordId}`),
    enabled: !!recordId,
    staleTime: PHOTO_STALE_TIME,
  });
}

/**
 * Group photos returned by `/api/ops/photos/{id}` by `fieldDataName`, preserving
 * Fulcrum form-order within each group. Returns the raw query plus a memoised
 * `groups` map keyed by the field data_name (e.g. `"photo_area"`, `"defect_photo"`,
 * `"repaired_photo"` for Tanami).
 *
 * Photos without a `fieldDataName` (legacy fallback response) land in `groups.unknown`.
 */
export function usePhotosByField(recordId: string | undefined) {
  const q = usePhotos(recordId);
  const groups = useMemo<Record<string, PhotoEntry[]>>(() => {
    const out: Record<string, PhotoEntry[]> = {};
    for (const p of q.data?.photos ?? []) {
      const key = p.fieldDataName ?? p.field ?? "unknown";
      (out[key] ??= []).push(p);
    }
    return out;
  }, [q.data]);
  return { ...q, groups };
}

export function useAuditTrail(recordId: string | undefined) {
  return useQuery<{ recordId: string; entries: AuditEntry[] }>({
    queryKey: ["ops", "audit", recordId],
    queryFn: () => fetchJson(`/api/ops/audit/${recordId}`),
    enabled: !!recordId,
    staleTime: STALE_TIME,
  });
}

export function useSyncStatus() {
  return useQuery<{ views: Record<string, SyncStatusEntry> }>({
    queryKey: ["ops", "sync-status"],
    queryFn: () => fetchJson("/api/ops/sync-status"),
    staleTime: SYNC_STALE_TIME,
    refetchInterval: SYNC_STALE_TIME,
  });
}

// ── Map tile configuration ──

export type MapBackgroundId = "azure-imagery" | "qld-sisp-public" | "site-ortho" | "azure-hybrid";

export interface MapBackgroundConfig {
  id: MapBackgroundId;
  displayName: string;
  sourceType: MapBackgroundId;
  tileUrl: string;
  nativeMinZoom: number;
  nativeMaxZoom: number;
  attribution: string;
}

export interface MapOverlayConfig {
  id: string;
  displayName: string;
  kind: "raster" | "vector";
  sourceType: string;
  defaultVisible: boolean;
  defaultOpacity: number;
  zOrder: number;
  tileUrl?: string;
  geojsonUrl?: string;
  nativeMaxZoom?: number;
  attribution?: string;
}

export interface MapDataLayerConfig {
  id: string;
  displayName: string;
  defaultVisible: boolean;
}

export interface SiteMapTileConfig {
  tileUrl: string;
  /** Azure Maps via tile proxy — underlay beneath custom orthophoto */
  basemapTileUrl?: string;
  /** Azure Maps roads/labels on transparent background — stack over imagery for a hybrid map. */
  hybridOverlayTileUrl?: string;
  center: [number, number];
  zoom: number;
  minZoom: number;
  maxZoom: number;
  /** Deepest z the tile source has; zooming past it upscales rather than blanking. */
  tileMaxZoom?: number;
  errorTileUrl: string;
  defaultBackgroundId?: MapBackgroundId;
  attribution?: string;
  comparisonNativeMaxZoom?: number;
  backgrounds?: MapBackgroundConfig[];
  overlays?: MapOverlayConfig[];
  dataLayers?: MapDataLayerConfig[];
}

export function useMapConfig() {
  return useQuery<{ sites: Record<string, SiteMapTileConfig> }>({
    queryKey: ["ops", "map-config"],
    queryFn: () => fetchJson("/api/ops/map-config"),
    staleTime: 5 * 60 * 1000, // 5 min — balance freshness vs. API load
  });
}

export function useMapBuildings(siteId: string | undefined, enabled: boolean) {
  return useQuery<{ type: "FeatureCollection"; features: unknown[] }>({
    queryKey: ["ops", "map-layers", siteId, "buildings"],
    queryFn: () => fetchJson(`/api/ops/map-layers/${siteId}/buildings`),
    enabled: Boolean(siteId) && enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

// ── Lookups — RecordLink choice lists (server-side cached 1hr) ──
export interface LookupItem {
  id: string;
  label: string;
}

export function useLookupRecords(formId: string | undefined) {
  return useQuery<{ items: LookupItem[] }>({
    queryKey: ["ops", "lookups", formId],
    queryFn: () => fetchJson(`/api/ops/lookups/${formId}`),
    enabled: !!formId,
    staleTime: 60 * 60 * 1000, // 1hr — lookups rarely change
    gcTime: 2 * 60 * 60 * 1000,
  });
}

// ── Aggregations (defect families, grades, repair, budget, risk matrix) ──

export function useAggregations(viewName: string | undefined) {
  return useQuery<AggregationStats>({
    queryKey: ["ops", "views", viewName, "aggregations"],
    queryFn: () => fetchJson(`/api/ops/views/${viewName}/aggregations`),
    enabled: !!viewName,
    staleTime: STALE_TIME,
  });
}

// ── Batch photo resolution (grid thumbnails) ──

/**
 * Resolve Fulcrum photo proxy URLs for ALL record IDs in the current page.
 * Chunks IDs into batches of 100 (backend MAX_IDS limit). Each chunk is a
 * separate React Query entry with its own staleTime — so cached chunks won't
 * re-fetch, and the server-side TTL cache protects against redundant Fulcrum hits.
 */
export function useBatchPhotos(viewName: string | undefined, recordIds: string[]) {
  const BACKEND_LIMIT = 100;

  const chunks = useMemo(() => {
    const result: string[][] = [];
    for (let i = 0; i < recordIds.length; i += BACKEND_LIMIT) {
      result.push(recordIds.slice(i, i + BACKEND_LIMIT));
    }
    return result;
  }, [recordIds]);

  const queries = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: ["ops", "photos", "batch", viewName, chunk.join(",")],
      queryFn: () =>
        fetchJson<{ photos: Record<string, string> }>(
          `/api/ops/photos/batch?ids=${chunk.join(",")}&viewName=${viewName!}`
        ),
      enabled: !!viewName && chunk.length > 0,
      staleTime: 10 * 60 * 1000, // 10 min — photo URLs are near-static
    })),
  });

  const data = useMemo(() => {
    const photos: Record<string, string> = {};
    for (const q of queries) {
      if (q.data?.photos) {
        Object.assign(photos, q.data.photos);
      }
    }
    return Object.keys(photos).length > 0 ? { photos } : undefined;
  }, [queries]);

  return {
    data,
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}
